import { Session, UUID } from '@benzinga/session';
import {
  CalendarCallType,
  CalendarEvents,
  CalendarDataParameters,
  CalendarManagerEvent,
  CalendarTransient,
  SetCalendarEvent,
  SetParametersEvent,
  CalendarFeedId,
  CalendarType,
  CalendarTypeEvents,
  CalendarDataParams,
  CalendarsRows,
  CalendarsRowsKeys,
  ErrorEvent,
  Saga,
  UpdateCalendarEvent,
} from './entities';
import { ParametersMap } from './generateManagerParams';
import { calendarDataParameters, calendarTransient, getSymbolsFromParameters } from './generateRestfulParams';
import { UnpackedArray } from '@benzinga/utils';
import { createSymbol } from '@benzinga/autocomplete-manager';

type ByFeed<T> = {
  [key in CalendarFeedId]: T;
};

export class CalendarStore {
  private parameters: ByFeed<CalendarDataParameters>;
  private transient: ByFeed<CalendarTransient>;
  private calendarsData: CalendarsRows;
  private calendarsDataRowsKeys: CalendarsRowsKeys;
  private saga: ByFeed<Saga>;

  constructor() {
    this.transient = {};
    this.parameters = {};
    this.calendarsData = {};
    this.calendarsDataRowsKeys = {};
    this.saga = {};
  }

  public getLastUpdateEventIds = (feedId: CalendarFeedId): CalendarEvents['id'][] | undefined =>
    this.transient[feedId]?.lastUpdatedEventsIds;

  public getIsLoading = (feedId: CalendarFeedId): boolean => !!this.transient[feedId]?.isLoading;

  public getEvents = (feedId: CalendarFeedId): CalendarEvents[] | undefined => {
    return this.transient[feedId]?.events;
  };

  public getParameters = (feedId: CalendarFeedId): CalendarDataParameters | undefined => this.parameters[feedId];

  public storeParameters = (event: CalendarManagerEvent, session: Session): void => {
    const { feedId, parameters, type } = event as SetParametersEvent;
    this.initFeed(feedId);
    const newParameters = { ...parameters };
    newParameters.filters = [...getSymbolsFromParameters(parameters, session)].map(symbol =>
      createSymbol({ name: symbol, symbol, type: 'symbol' }),
    );
    if (type === CalendarCallType.SET_PARAMETERS || type === CalendarCallType.UPDATE_EVENTS) {
      this.parameters[feedId] = { ...this.getParameters(feedId), ...newParameters };
    }
  };

  public generateParametersMap = (calendarType: CalendarType): ParametersMap => {
    const parametersMap: ParametersMap = new Map();
    Object.keys(this.parameters).forEach(feedId => {
      const events = this.transient[feedId]?.events;
      const parameters = this.parameters[feedId];
      if (parameters?.calendarType === calendarType && events?.length) {
        parametersMap.set(feedId, { events, parameters });
      }
    });

    return parametersMap;
  };

  public setEvents = (
    feedId: CalendarFeedId,
    events: CalendarEvents[],
    parameters?: CalendarDataParameters,
    lastUpdatedEventsIds: CalendarEvents['id'][] = [],
  ): void => {
    this.initFeed(feedId);
    this.transient[feedId].events = events;
    this.transient[feedId].lastUpdatedEventsIds = lastUpdatedEventsIds;
    this.transient[feedId].isLoading = false;
    if (parameters) {
      this.parameters[feedId] = parameters;
    }
  };

  public setCalendarData = <T extends CalendarType>(
    calendarType: T,
    calendarParams: CalendarDataParams,
    calendarData: Required<CalendarTypeEvents>[T],
  ): CalendarsRowsKeys => {
    const calendarParamsIdentifierKey = this.formatCalendarParamsIdentifier(calendarParams);

    if (!this.calendarsData[calendarType]) {
      this.calendarsData[calendarType] = new Map();
    }
    const row = this.calendarsData[calendarType] as Map<
      string,
      WeakRef<UnpackedArray<Required<CalendarTypeEvents>[T]>>
    >;

    const safeRows = Array.isArray(calendarData)
      ? calendarData.filter(
          (item): item is UnpackedArray<Required<CalendarTypeEvents>[T]> => item != null && item.id != null,
        )
      : [];

    if (!this.calendarsDataRowsKeys[calendarType]) {
      this.calendarsDataRowsKeys[calendarType] = new Map();
      this.calendarsDataRowsKeys[calendarType].set(
        calendarParamsIdentifierKey,
        safeRows.map(item => item.id),
      );
    }

    safeRows.forEach(calendarRow => {
      const weak = new WeakRef(calendarRow as UnpackedArray<Required<CalendarTypeEvents>[T]>);
      row.set(calendarRow.id, weak);
    });

    return this.calendarsDataRowsKeys;
  };

  public getCalendarData = <T extends CalendarType>(
    calendarType: T,
    calendarParams: CalendarDataParams,
  ): CalendarTypeEvents[T] | null => {
    const calendarParamsIdentifierKey = this.formatCalendarParamsIdentifier(calendarParams);

    if (this.calendarsDataRowsKeys[calendarType]) {
      const rowsKeys = this.calendarsDataRowsKeys[calendarType].get(calendarParamsIdentifierKey);

      const rows: Required<CalendarTypeEvents>[T] = [];

      if (rowsKeys) {
        rowsKeys.forEach((key: string) => {
          const calMap = this.calendarsData[calendarType];
          if (calMap?.has(key)) {
            const row = calMap?.get(key);
            const rowRef = row?.deref();
            if (rowRef) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              rows.push(rowRef);
            }
          }
        });
        return rows;
      }
    }
    return null;
  };

  public storeEvents = (event: CalendarManagerEvent): void => {
    const {
      events = [],
      feedId,
      lastUpdatedEventsIds = [],
      parameters,
      type,
    } = event as SetCalendarEvent | UpdateCalendarEvent | ErrorEvent;
    if (
      type === CalendarCallType.SET_EVENTS ||
      type === CalendarCallType.UPDATE_EVENTS ||
      type === CalendarCallType.ERROR
    ) {
      this.setEvents(feedId, events, parameters, lastUpdatedEventsIds);
    }
  };

  public setSaga = (feedId: CalendarFeedId, id: UUID = '', batchLoaded = false): void => {
    this.initFeed(feedId);
    this.saga[feedId] = { batchLoaded, id };
  };

  public getSaga = (feedId: CalendarFeedId): Saga | undefined => this.saga[feedId];

  public setUpdateInterval = (
    feedId: CalendarFeedId,
    updateInterval?: ReturnType<typeof setTimeout>,
    intervalCallback?: VoidFunction | null, // null will clear callback
  ): void => {
    this.initFeed(feedId);
    this.transient[feedId].updateInterval = updateInterval;
    if (intervalCallback !== undefined) {
      this.transient[feedId].intervalCallback = intervalCallback;
    }
  };

  public getUpdateInterval = (feedId: CalendarFeedId): ReturnType<typeof setTimeout> | undefined =>
    this.transient[feedId]?.updateInterval;

  public getIntervalCallback = (feedId: CalendarFeedId): VoidFunction | undefined | null =>
    this.transient[feedId]?.intervalCallback;

  private initFeed = (feedId: CalendarFeedId): void => {
    if (!this.transient[feedId]) {
      this.transient[feedId] = { ...calendarTransient };
    }
    if (!this.parameters[feedId]) {
      this.parameters[feedId] = { ...calendarDataParameters };
    }
  };

  private formatCalendarParamsIdentifier = (calendarParams: CalendarDataParams): string => {
    let calendarParamsIdentifierKey = '';
    if (calendarParams) {
      for (const [, value] of Object.entries(calendarParams).sort()) {
        calendarParamsIdentifierKey += value;
      }
    }
    return calendarParamsIdentifierKey;
  };
}
